import { useState, useRef, useEffect } from "react";
import { IconChevronDown, IconX } from "@tabler/icons-react";
import { cn } from "@/lib/utils";

/**
 * A combobox for picking a geographic value (district / upazila).
 * - Clicking or typing opens a filtered dropdown of suggestions.
 * - Selecting a suggestion fills the input.
 *
 * By default the user may also type a custom value not in the list.
 * Pass `selectOnly` to restrict input to list selection only: typing then
 * only filters the suggestions, and the committed value changes ONLY when an
 * option is chosen. Any unmatched typed text is discarded on blur, so the
 * previously stored value (including legacy free-text values) is preserved.
 */
export default function GeoCombobox({
    value = "",
    onChange,
    onSelectOption,      // optional: called with the full option object on pick
    onEnter,             // optional: called with the typed value when Enter is pressed
    onClear,             // optional: called when the × clear button is pressed
    options = [],        // [{ label, value, hint? }] or string[]
    placeholder = "",
    className = "",
    disabled = false,
    selectOnly = false,
    clearable = false,   // show an × to clear the typed value (free-text mode)
}) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const containerRef = useRef(null);

    // Sync external value → input (e.g., when district changes and thana is reset)
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Close dropdown on outside click. In selectOnly mode, discard any unmatched
    // typed text by reverting the input back to the committed value.
    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                if (selectOnly) setInputValue(value);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [selectOnly, value]);

    const normalizedOptions = options.map((o) =>
        typeof o === "string" ? { label: o, value: o } : o
    );

    // In selectOnly mode `inputValue` is independent of the committed value, so
    // when it still equals that value (box just opened) show the full list and
    // only filter once the user actually types. In free-text mode `inputValue`
    // tracks the committed value, so always filter by it.
    const query = inputValue.trim().toLowerCase();
    const filtered = query && (!selectOnly || query !== value.toLowerCase())
        ? normalizedOptions.filter(
              (o) =>
                  o.label.toLowerCase().includes(query) ||
                  (o.en && o.en.toLowerCase().includes(query))
          )
        : normalizedOptions;

    const handleInputChange = (e) => {
        const v = e.target.value;
        setInputValue(v);
        if (!selectOnly) onChange(v);
        setOpen(true);
    };

    const handleSelect = (opt) => {
        setInputValue(opt.label);
        onChange(opt.label);
        onSelectOption?.(opt);
        setOpen(false);
    };

    const handleBlur = () => {
        // Revert unmatched typed text; the list's onMouseDown+preventDefault
        // means a real selection commits before this blur fires.
        if (selectOnly) setInputValue(value);
    };

    // Clear the typed value; onMouseDown+preventDefault keeps input focus so the
    // suggestion list stays open for the next pick.
    const showClear = clearable && !selectOnly && !disabled && inputValue !== "";
    const handleClear = () => {
        setInputValue("");
        onChange("");
        onClear?.();
        setOpen(true);
    };

    const inputCls =
        "w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md text-sm " +
        "focus:outline-none focus:ring-2 focus:ring-orange-400 " +
        "bg-white dark:bg-zinc-800 dark:text-zinc-100 " +
        "placeholder:text-zinc-400 dark:placeholder:text-zinc-500 " +
        "disabled:opacity-50 disabled:cursor-not-allowed" +
        (selectOnly || clearable ? " pr-9" : "");

    return (
        <div ref={containerRef} className="relative">
            <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => setOpen(true)}
                onClick={() => setOpen(true)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && onEnter) {
                        e.preventDefault();
                        onEnter(inputValue);
                    }
                }}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled}
                className={cn(inputCls, className)}
                autoComplete="off"
                readOnly={selectOnly && !open}
            />

            {selectOnly && (
                <IconChevronDown
                    size={16}
                    className={cn(
                        "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 transition-transform",
                        open && "rotate-180"
                    )}
                />
            )}

            {showClear && (
                <button
                    type="button"
                    tabIndex={-1}
                    aria-label="Clear"
                    onMouseDown={(e) => {
                        e.preventDefault(); // keep input focus so the list stays open
                        handleClear();
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300"
                >
                    <IconX size={15} />
                </button>
            )}

            {open && filtered.length > 0 && (
                <ul className="absolute z-[300] left-0 right-0 mt-1 max-h-52 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg text-sm">
                    {filtered.map((opt) => (
                        <li
                            key={opt.value}
                            onMouseDown={(e) => {
                                e.preventDefault(); // prevent input blur before click
                                handleSelect(opt);
                            }}
                            className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-800 dark:text-zinc-100"
                        >
                            <span className="min-w-0 truncate">{opt.label}</span>
                            {opt.hint && (
                                <span className="flex-shrink-0 text-xs font-medium text-zinc-400 dark:text-zinc-500 tabular-nums">
                                    {opt.hint}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
